import { useCallback, useMemo, useState } from 'react';
import { BookOpen, Landmark, Plus, School, Sparkles } from 'lucide-react';
import ExerciseurCard from '../../VueQuestion/ExerciseurCard';
import SectionPliable from '../../VueQuestion/SectionPliable';
import QuestionCardEvalNat from './QuestionCardEvalNat';
import FenetreTexteSupport from './FenetreTexteSupport';
import LigneTexteLecture from './LigneTexteLecture';
import { aUnGenerateur, formatASaisir, genererVariante, texteDeLItem, varianteFixe } from '../../../generators/evalnat';
import { versVarianteEvalNat } from '../../../utils/evalNatAdapter';

/**
 * Un item de l'évaluation nationale dans le QCM de suivi (11/10/2026), mis en page comme un attendu de
 * QCMDashboard (AttenduExercices), en attendant l'interface commune : la question officielle en tête, puis
 * la section « Exerciseurs TanoEd » avec la MÊME carte (ExerciseurCard, ses variantes tirées par le
 * générateur de l'item), les variantes du collège, et « Créer une question personnalisée ».
 */
const LIBELLES_A_SAISIR = { saisie: 'Saisie', trous: 'Texte à trous' };
const ESSAIS = 3;

const ItemEvalNat = ({
  item, niveau, niveauForm, matiere, selectedExercices, onUpdateExercice, selectedIds, onToggleSelect,
  onPreview, onApercu, onEditVariante, onDeleteVariante, onCreateQuestion, nouveauId,
}) => {
  const { officielle, variantes, questionNum, typeLabel } = item;
  const libelle = (officielle || variantes[0])?.nom_label || '';
  const avecExerciseur = Boolean(officielle && aUnGenerateur(officielle, niveau));
  const [sectionOuverte, setSectionOuverte] = useState(true);
  const [texteOuvert, setTexteOuvert] = useState(false);

  // La carte de l'item : le format QCM, et Saisie ou Texte à trous quand l'item en a un
  const categorie = useMemo(() => {
    if (!avecExerciseur) return null;
    const aSaisir = formatASaisir(officielle, niveau);
    return {
      id: questionNum,
      label: `${questionNum} · ${libelle}`,
      formatsList: aSaisir
        ? [{ key: 'qcm', label: 'QCM' }, { key: 'saisie', label: LIBELLES_A_SAISIR[aSaisir] }]
        : [{ key: 'qcm', label: 'QCM' }],
    };
  }, [avecExerciseur, officielle, niveau, questionNum, libelle]);

  // Un tirage de l'item, prêt à devenir une variante (sinon un nouvel essai) ; lève si l'exerciseur échoue
  const genererExercice = useCallback((format) => {
    let erreur = null;
    for (let essai = 0; essai < ESSAIS; essai += 1) {
      try {
        const payload = genererVariante(officielle, niveau, format);
        versVarianteEvalNat(payload);
        return payload;
      } catch (e) {
        erreur = e;
      }
    }
    throw erreur;
  }, [officielle, niveau]);

  // La carte donne la clé et le compte ; la page a besoin de l'item d'origine
  const ajouter = useCallback(
    (cle, compte, payload) => onUpdateExercice(cle, compte, payload, officielle),
    [onUpdateExercice, officielle]
  );

  // Le nouveau texte des variantes (compréhension de l'écrit) : au-dessus de la carte, et en haut de sa
  // fenêtre « Modifier »
  const texte = avecExerciseur ? texteDeLItem(officielle, niveau) : null;
  const enTeteEdition = useMemo(() => (texte ? <LigneTexteLecture support={texte} /> : null), [texte]);

  return (
    <div className="space-y-6">
      {/* La question officielle d'abord (prof, 11/10) : c'est elle que les variantes reprennent */}
      {officielle ? (
        <div>
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-2.5 py-1 text-xs font-semibold mb-3">
            <Landmark size={13} />
            Question officielle · {questionNum}
          </span>
          <div className="columns-1 lg:columns-2 gap-4 space-y-4">
            <QuestionCardEvalNat
              question={officielle}
              selectable
              selected={selectedIds.has(officielle.id)}
              onToggle={onToggleSelect}
              onPreview={onPreview}
            />
          </div>
        </div>
      ) : null}

      {avecExerciseur ? (
        <SectionPliable
          titre="Exerciseurs TanoEd"
          icone={<Sparkles size={13} />}
          compteur={1}
          couleur="blue"
          ouvert={sectionOuverte}
          onToggle={() => setSectionOuverte((v) => !v)}
        >
          {texte ? (
            <button
              type="button"
              onClick={() => setTexteOuvert(true)}
              className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 hover:bg-blue-100 transition"
            >
              <BookOpen size={13} />
              Le nouveau texte de la variante : {texte.titre} · Lire le texte
            </button>
          ) : null}
          <div className="columns-1 lg:columns-2 gap-4 space-y-4">
            <ExerciseurCard
              key={`evalnat_${officielle.id}`}
              category={categorie}
              niveau={niveauForm}
              matiere={matiere}
              niveauChar={niveau}
              selectable
              exoKeyPrefix={`evalnat_${officielle.id}`}
              selectedExercices={selectedExercices}
              onUpdateExercice={ajouter}
              onPreview={onApercu}
              descName={typeLabel}
              attenduDescription={libelle}
              genererExercice={genererExercice}
              varianteUnique={varianteFixe(officielle, niveau)}
              sansModes
              enTeteEdition={enTeteEdition}
            />
          </div>
        </SectionPliable>
      ) : null}

      {variantes.length > 0 ? (
        <div>
          <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-full px-2.5 py-1 text-xs font-semibold mb-3">
            <School size={13} />
            Variantes du collège · {variantes.length}
          </span>
          <div className="columns-1 lg:columns-2 gap-4 space-y-4">
            {variantes.map((v) => (
              <div key={v.id} className={`break-inside-avoid ${v.id === nouveauId ? 'rounded-xl ring-2 ring-amber-300' : ''}`}>
                <QuestionCardEvalNat
                  question={v}
                  selectable
                  selected={selectedIds.has(v.id)}
                  onToggle={onToggleSelect}
                  onPreview={onPreview}
                  onEdit={onEditVariante}
                  onDelete={onDeleteVariante}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {officielle ? (
        <button
          type="button"
          onClick={() => onCreateQuestion(officielle)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-blue-400 bg-white text-blue-700 hover:border-blue-600 hover:bg-blue-50 text-[15px] font-bold shadow-sm transition-all"
        >
          <Plus size={16} />
          Créer une question personnalisée
        </button>
      ) : null}

      {/* Espace pour la barre du bas */}
      <div className="h-24" />

      {texte && texteOuvert ? <FenetreTexteSupport support={texte} onClose={() => setTexteOuvert(false)} /> : null}
    </div>
  );
};

export default ItemEvalNat;
